<template>
  <section class="ai-conclusion flex flex-col h-full rounded overflow-hidden">
    <!-- Header -->
    <div class="ai-conclusion__header flex items-start gap-2.5">
      <svg-icon
        name="huge-ai"
        class="ai-conclusion__icon w-4 h-4 shrink-0 fill-none mt-1"
      />
      <div class="flex-1 min-w-0">
        <div class="ai-conclusion__title-row flex items-center gap-2 flex-wrap">
          <div class="ai-conclusion__title">
            {{ $t("Заключение AI") }}
          </div>

          <!-- Stale badge — на уровне title -->
          <Tag
            v-if="isStale"
            variant="warning"
            size="xs"
            class="rounded shrink-0 !bg-orange-50/60 !border-orange-200 !text-orange-600"
          >
            {{ $t("Устарело") }}
          </Tag>

          <!-- Тег результата — на уровне title -->
          <template v-if="hasAiResult">
            <Tag
              v-if="hasCritics"
              variant="danger"
              size="xs"
              class="rounded shrink-0"
            >
              {{ $t("Найдены замечания") }}
            </Tag>
            <Tag
              v-else-if="verdictStatus === 'requires_manual_review'"
              variant="warning"
              size="xs"
              class="rounded shrink-0 !bg-yellow-50/60 !border-yellow-200 !text-yellow-700"
            >
              {{ $t("Требуется проверка") }}
            </Tag>
            <Tag v-else variant="success" size="xs" class="rounded shrink-0">
              {{ $t("Замечаний нет") }}
            </Tag>
          </template>

          <Tag
            v-else-if="state === 'running' || state === 'triggering'"
            variant="primary"
            size="xs"
            class="rounded shrink-0 !bg-indigo-50/60 !border-indigo-200 !text-indigo-600"
          >
            {{ $t("Анализ") }}
          </Tag>
        </div>

        <p class="ai-conclusion__subtitle truncate">
          <template
            v-if="!hasAiResult && state !== 'running' && state !== 'triggering'"
          >
            {{
              $t(
                "AI-проверка ещё не запускалась. Нажмите «Запустить проверку», чтобы получить заключение."
              )
            }}
          </template>
          <template v-else-if="state === 'triggering'">
            {{ $t("Отправляем документы в сервис") }}
          </template>
          <template v-else-if="state === 'running'">
            {{ $t("AI анализирует пакет документов") }}
          </template>
          <template v-else-if="lastCheckedAt">
            {{ $t("Последняя проверка") }}: {{ lastCheckedAt }}
          </template>
        </p>
      </div>
    </div>

    <!-- Body -->
    <div class="ai-conclusion__body flex-1 flex flex-col gap-3.5">
      <!-- Summary -->
      <p class="ai-conclusion__summary">
        {{ displayedSummary
        }}<AiCheckBlinkCursor
          v-if="showCaret || (typewriterEnabled && isTyping)"
        />
      </p>

      <!-- Empty state когда done но замечаний нет — показываем дружелюбное сообщение -->
      <p
        v-if="
          hasAiResult &&
          !criticalSignals.length &&
          !warningSignals.length &&
          !okSignals.length
        "
        class="ai-conclusion__empty"
      >
        {{ $t("AI не нашёл проблем в документах.") }}
      </p>

      <!-- Замечание = critical signals (red) -->
      <div
        v-if="hasAiResult && criticalSignals.length"
        class="flex flex-col gap-1.5 ai-reveal"
        :style="{ animationDelay: `${problemsBaseDelay}ms` }"
      >
        <div
          class="ai-conclusion__section-title ai-conclusion__section-title--bad"
        >
          {{ $t("Замечания") }}
        </div>
        <ul class="flex flex-col gap-1.5 list-none p-0 m-0">
          <li
            v-for="(sig, i) in criticalSignals"
            :key="sig.id"
            class="ai-conclusion__signal ai-reveal"
            :style="{
              animationDelay: `${problemsBaseDelay + 150 + i * 200}ms`,
            }"
          >
            <div
              class="ai-conclusion__dot ai-conclusion__dot--bad shrink-0"
            ></div>
            <div class="flex-1 min-w-0">
              <div class="ai-conclusion__signal-text">
                {{ sig.title }}
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Внимание = warning signals (yellow) -->
      <div
        v-if="hasAiResult && warningSignals.length"
        class="flex flex-col gap-1.5 ai-reveal"
        :style="{ animationDelay: `${recommendationsBaseDelay}ms` }"
      >
        <div
          class="ai-conclusion__section-title ai-conclusion__section-title--warn"
        >
          {{ $t("Предупреждения") }}
        </div>
        <ul class="flex flex-col gap-1.5 list-none p-0 m-0">
          <li
            v-for="(sig, i) in warningSignals"
            :key="sig.id"
            class="ai-conclusion__signal ai-reveal"
            :style="{
              animationDelay: `${recommendationsBaseDelay + 150 + i * 200}ms`,
            }"
          >
            <div
              class="ai-conclusion__dot ai-conclusion__dot--warn shrink-0"
            ></div>
            <div class="flex-1 min-w-0">
              <div class="ai-conclusion__signal-text">
                {{ sig.title }}
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Чисто = ok signals (green) -->
      <div
        v-if="hasAiResult && okSignals.length"
        class="flex flex-col gap-1.5 ai-reveal"
        :style="{ animationDelay: `${okBaseDelay}ms` }"
      >
        <div
          class="ai-conclusion__section-title ai-conclusion__section-title--ok"
        >
          {{ $t("Успешно") }}
        </div>
        <ul class="flex flex-col gap-1.5 list-none p-0 m-0">
          <li
            v-for="(sig, i) in okSignals"
            :key="sig.id"
            class="ai-conclusion__signal ai-reveal"
            :style="{
              animationDelay: `${okBaseDelay + 150 + i * 200}ms`,
            }"
          >
            <div
              class="ai-conclusion__dot ai-conclusion__dot--ok shrink-0"
            ></div>
            <div class="flex-1 min-w-0">
              <div class="ai-conclusion__signal-text">
                {{ sig.title }}
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Tag } from "@/shared/ui/tag";
import AiCheckBlinkCursor from "./AiCheckBlinkCursor.vue";
import { useTypewriter } from "./useTypewriter";

const { t } = useI18n();

const props = defineProps({
  createdAt: { type: String, default: "" },
  lastCheckedAt: { type: String, default: "" },
  summary: { type: String, default: "" },
  signals: { type: Array, default: () => [] },
  state: { type: String, default: "unchecked" },
  hasAiResult: { type: Boolean, default: false },
  verdictStatus: { type: String, default: null },
  isStale: { type: Boolean, default: false },
  newDocsCount: { type: Number, default: 0 },
  enableTypewriter: { type: Boolean, default: false },
});

defineEmits(["regenerate"]);

const criticalSignals = computed(() =>
  props.signals.filter((s) => s.severity === "bad")
);
const warningSignals = computed(() =>
  props.signals.filter((s) => s.severity === "warn")
);
const okSignals = computed(() =>
  props.signals.filter((s) => s.severity === "ok")
);
const hasCritics = computed(() => criticalSignals.value.length > 0);

const summaryText = computed(() => {
  if (
    !props.hasAiResult &&
    props.state !== "running" &&
    props.state !== "triggering"
  ) {
    return "";
  }
  if (props.state === "triggering") return t("Запускаем AI-проверку…");
  if (props.state === "running") {
    return t(
      "AI сверяет реквизиты, ищет подписи, оценивает бесспорность требования"
    );
  }
  return props.summary;
});

const showCaret = computed(
  () => props.state === "running" || props.state === "triggering"
);

const isDone = computed(
  () => props.state === "done" || props.state === "stale"
);
const typewriterEnabled = computed(
  () => isDone.value && props.enableTypewriter
);
const { typedText, isTyping } = useTypewriter(summaryText, {
  speed: 18,
  delay: 200,
  enabled: typewriterEnabled,
});

const displayedSummary = computed(() => {
  if (typewriterEnabled.value) return typedText.value;
  return summaryText.value;
});

const summaryEndMs = computed(() => {
  if (!typewriterEnabled.value) return 0;
  return (summaryText.value?.length || 0) * 18 + 250;
});

const problemsBaseDelay = computed(() => summaryEndMs.value);
const recommendationsBaseDelay = computed(() => {
  const itemsTime = criticalSignals.value.length * 200 + 400;
  return summaryEndMs.value + itemsTime;
});
const okBaseDelay = computed(() => {
  const itemsTime = warningSignals.value.length * 200 + 400;
  return recommendationsBaseDelay.value + itemsTime;
});
</script>

<style lang="scss" scoped>
.ai-conclusion {
  @apply border border-solid border-border bg-muted/30;

  &__header {
    @apply border-0 border-b border-solid border-border;
    padding: 14px 14px;
    background: transparent;
  }

  &__icon {
    color: rgb(var(--color-indigo-500));
  }

  &__title {
    @apply font-semibold;
    font-size: 13px;
    letter-spacing: -0.005em;
    color: rgb(var(--color-gray-700));
    margin: 0;
  }

  &__subtitle {
    font-size: 12px;
    color: rgb(var(--color-gray-500));
    margin: 2px 0 0 0;
    line-height: 1.4;
  }

  &__body {
    padding: 12px 14px 14px;
    @apply bg-card;
  }

  &__summary {
    font-size: 12.5px;
    line-height: 1.55;
    color: rgb(var(--color-gray-600));
    margin: 0;
  }

  &__empty {
    font-size: 12px;
    line-height: 1.5;
    color: rgb(var(--color-gray-500));
    margin: 0;
  }

  &__section-title {
    @apply font-medium uppercase;
    font-size: 10px;
    letter-spacing: 0.04em;

    &--bad {
      color: rgb(var(--color-red-500));
    }

    &--warn {
      color: rgb(var(--color-yellow-600));
    }

    &--ok {
      color: rgb(var(--color-green-500));
    }
  }

  &__signal {
    @apply flex items-start gap-2;
  }

  &__signal-text {
    font-size: 12px;
    line-height: 1.5;
    color: rgb(var(--color-gray-600));
  }

  &__dot {
    width: 4px;
    height: 4px;
    border-radius: 9999px;
    margin-top: 7px;

    &--bad {
      background: rgb(var(--color-red-300));
    }

    &--warn {
      background: rgb(var(--color-yellow-400));
    }

    &--ok {
      background: rgb(var(--color-green-300));
    }
  }
}

.ai-reveal {
  opacity: 0;
  transform: translateY(4px);
  animation: ai-content-reveal 500ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes ai-content-reveal {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
